// Pure utility: group an array of WorkOrderV2 records for the consolidated
// list view.
//
// Grouping key depends on the `groupByDate` option:
//   - groupByDate=true  (default — matches v1 behaviour):
//       output_item.id + operation.id + due_date(day)
//   - groupByDate=false:
//       output_item.id + operation.id  (multiple due dates summarised on one row)
//
// Operation is taken from the first step on the WO when present.
//
// Input: array of WorkOrderV2 records (as returned by /api/v2/work-orders/).
// Output: array of grouped rows:
//   {
//     key,                   // stable composite key
//     output_item_id,
//     output_item_code,
//     output_item_name,
//     operation_id,
//     operation_code,
//     operation_name,
//     due_date,              // 'YYYY-MM-DD' or null — when groupByDate=false this
//                            //  is the earliest non-null date present (or null)
//     dates,                 // sorted list of distinct 'YYYY-MM-DD' values
//                            //  present in the group (nulls excluded). Always
//                            //  populated so callers can show "+N more dates".
//     total_quantity,        // sum of quantity_to_produce across sources
//     source_count,
//     statuses,              // distinct status values present
//     sources: [             // one entry per underlying WO
//       {
//         work_order_id,
//         wo_number,
//         quantity_to_produce,
//         status,
//         tree_id,
//         customer_order_id,
//         customer_order_number,
//         customer_order_line_id,
//         line_number,
//         due_date,
//       },
//       ...
//     ],
//   }

function dateKey(dueDate) {
  if (!dueDate) return 'no_due_date';
  // dueDate may be 'YYYY-MM-DD' (preferred) or an ISO timestamp.
  if (typeof dueDate === 'string' && dueDate.length >= 10) {
    return dueDate.slice(0, 10);
  }
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return 'no_due_date';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function firstStepOperation(wo) {
  if (!wo || !Array.isArray(wo.steps) || wo.steps.length === 0) {
    return { id: null, code: '', name: '' };
  }
  const first = [...wo.steps].sort((a, b) => (a.sequence || 0) - (b.sequence || 0))[0];
  return {
    id: first.operation ?? null,
    code: first.operation_code || '',
    name: first.operation_name || '',
  };
}

function toNumber(v) {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

export function groupForConsolidatedView(workOrders, options = {}) {
  const { groupByDate = true, dateDescending = false } = options;
  if (!Array.isArray(workOrders) || workOrders.length === 0) return [];

  const groups = new Map();

  for (const wo of workOrders) {
    if (!wo) continue;
    const op = firstStepOperation(wo);
    const dk = dateKey(wo.due_date);
    const outputId = wo.output_item ?? null;
    const key = groupByDate
      ? `${outputId ?? 'null'}|${op.id ?? 'null'}|${dk}`
      : `${outputId ?? 'null'}|${op.id ?? 'null'}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        // First-encounter index in the (already user-sorted) input — the
        // group inherits the caller's "Sort by" ordering through this.
        _rank: groups.size,
        output_item_id: outputId,
        output_item_code: wo.output_item_code || '',
        output_item_name: wo.output_item_name || '',
        operation_id: op.id,
        operation_code: op.code,
        operation_name: op.name,
        // For groupByDate=true the group's date is the shared key date; for
        // groupByDate=false we'll fill the earliest concrete date after the
        // loop. Track _dateSet to dedupe.
        due_date: groupByDate ? (dk === 'no_due_date' ? null : dk) : null,
        _dateSet: new Set(),
        total_quantity: 0,
        source_count: 0,
        statuses: new Set(),
        sources: [],
      });
    }
    const g = groups.get(key);
    g.total_quantity += toNumber(wo.quantity_to_produce);
    g.source_count += 1;
    if (wo.status) g.statuses.add(wo.status);
    if (dk !== 'no_due_date') g._dateSet.add(dk);
    g.sources.push({
      work_order_id: wo.id,
      // Step identity matters: a multi-step WO contributes one source per
      // step (different operations → different groups), and consumers must
      // resolve the RIGHT step, not just any step of the WO.
      step_id: wo.step_id ?? null,
      wo_number: wo.wo_number,
      quantity_to_produce: toNumber(wo.quantity_to_produce),
      status: wo.status,
      tree_id: wo.tree_id,
      customer_order_id: wo.customer_order ?? null,
      customer_order_number: wo.customer_order_number || '',
      customer_order_line_id: wo.customer_order_line ?? null,
      line_number: wo.line_number ?? null,
      due_date: wo.due_date || null,
    });
  }

  const out = Array.from(groups.values()).map((g) => {
    const dates = Array.from(g._dateSet).sort();
    let due = g.due_date;
    // When groupByDate=false the group's nominal due_date is the earliest
    // concrete date in the group (or null if every source is undated).
    if (!groupByDate) {
      due = dates.length > 0 ? dates[0] : null;
    }
    const { _dateSet, ...rest } = g; // eslint-disable-line no-unused-vars
    return {
      ...rest,
      due_date: due,
      dates,
      statuses: Array.from(g.statuses),
    };
  });

  // Ordering contract: the caller passes rows ALREADY sorted by the user's
  // chosen "Sort by" (urgency, item name, WO#, due date, ...). Groups
  // inherit that ordering via _rank (first-encounter index) rather than
  // imposing their own — imposing item-code/date order here is what used to
  // make the Sort-by dropdown a no-op in consolidated view.
  //   - groupByDate=true: date sections stay chronological (descending when
  //     the caller sorts by due date desc; undated always last); WITHIN a
  //     date, _rank decides.
  //   - groupByDate=false: _rank alone decides.
  let sorted;
  if (groupByDate) {
    sorted = out.sort((a, b) => {
      // null due dates last
      if (!a.due_date && b.due_date) return 1;
      if (a.due_date && !b.due_date) return -1;
      if (a.due_date && b.due_date && a.due_date !== b.due_date) {
        const cmp = a.due_date < b.due_date ? -1 : 1;
        return dateDescending ? -cmp : cmp;
      }
      return a._rank - b._rank;
    });
  } else {
    sorted = out.sort((a, b) => a._rank - b._rank);
  }
  return sorted.map(({ _rank, ...rest }) => rest); // eslint-disable-line no-unused-vars
}

export default groupForConsolidatedView;
